import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, Calendar } from "lucide-react";
import { langByCode, CATEGORIES } from "../lib/blogHelpers";
import BlogReader from "./BlogReader";

export default function BlogPreviewModal({ open, onClose, blog }) {
  const langs = Object.keys(blog?.translations || {});
  const initialLang = langs.includes(blog?.originalLang)
    ? blog.originalLang
    : langs[0] || "en";
  const [activeLang, setActiveLang] = useState(initialLang);

  if (!blog) return null;

  const tr = blog.translations[activeLang] || {};
  const cat = CATEGORIES.find((c) => c.code === blog.category);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-[#1C1A18]/70 backdrop-blur-sm overflow-y-auto"
          onClick={onClose}
          data-testid="blog-preview-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="min-h-full bg-[#FAF9F6] blog-protect"
            data-testid="blog-preview-modal"
          >
            {/* Preview ribbon */}
            <div className="sticky top-0 z-10 bg-[#2C2A28] text-[#FAF9F6] px-5 py-3 flex items-center justify-between border-b border-[#4A6B53]">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase">
                <Eye size={13} strokeWidth={1.8} />
                Preview · how readers will see this post
              </span>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-xs hover:text-[#CC7A52] transition-colors"
                data-testid="blog-preview-close"
              >
                <X size={14} /> Close preview
              </button>
            </div>

            {/* Hero */}
            <section className="relative">
              {blog.coverImage ? (
                <div className="relative h-[40vh] min-h-[280px] overflow-hidden">
                  <img
                    src={blog.coverImage}
                    alt={tr.title}
                    className="w-full h-full object-cover no-copy"
                    draggable="false"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(to bottom, rgba(44,42,40,0.15) 0%, rgba(44,42,40,0.55) 70%, rgba(44,42,40,0.85) 100%)",
                    }}
                  />
                  <div className="absolute inset-x-0 bottom-0">
                    <div className="container-narrow pb-8">
                      {cat && (
                        <p className="eyebrow !text-[#CC7A52]">Blog · {cat.name}</p>
                      )}
                      <h1 className="font-serif text-[#FAF9F6] mt-3 text-3xl sm:text-5xl leading-[1.05] tracking-tight max-w-4xl">
                        {tr.title || "(Untitled)"}
                      </h1>
                      <p className="mt-3 text-[#EAE5DC] text-sm">
                        Author: {blog.authorName || "Rajib Sengupta"}
                      </p>
                      <p className="mt-1 text-[#EAE5DC] text-sm inline-flex items-center gap-1.5">
                        <Calendar size={13} /> Will be published today
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="container-narrow pt-14 pb-6">
                  {cat && <p className="eyebrow">Blog · {cat.name}</p>}
                  <h1 className="font-serif mt-3 text-4xl sm:text-5xl text-[#2C2A28] leading-tight">
                    {tr.title || "(Untitled)"}
                  </h1>
                  <p className="mt-3 text-sm text-[#5C5852]">
                    Author: {blog.authorName || "Rajib Sengupta"}
                  </p>
                </div>
              )}
            </section>

            {/* Language tabs */}
            {langs.length > 1 && (
              <section className="pt-6">
                <div className="container-narrow">
                  <div className="inline-flex items-center gap-1 bg-[#F2EFEB] border border-[#D8D3CC] rounded-sm p-1">
                    {langs.map((code) => {
                      const meta = langByCode(code);
                      const active = code === activeLang;
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() => setActiveLang(code)}
                          className={`px-3 py-1.5 rounded-sm text-sm tracking-wide transition-colors ${
                            active
                              ? "bg-[#4A6B53] text-[#FAF9F6]"
                              : "text-[#2C2A28] hover:text-[#4A6B53]"
                          }`}
                          data-testid={`preview-lang-${code}`}
                        >
                          {meta.native}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            <BlogReader blog={blog} activeLang={activeLang} />

            <div className="h-16" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
